import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Check, Crown, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const PERKS = [
  'Position & Strategy Analyzer',
  'Strategy & Credit Scanners',
  'RollMaster & Roll Calculator',
  'AI Stock & Strategy Analysis',
  'Portfolio import & health tools',
  'P/L charts & market scans',
];

const PlanSelectionModal = ({ open, onOpenChange }) => {
  const { plans, startCheckout } = useSubscription();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);

  const monthly = plans.find((p) => p.plan_type === 'monthly');
  const annual = plans.find((p) => p.plan_type === 'annual');

  const handleChoose = async (lookupKey) => {
    if (!isAuthenticated) {
      toast.info('Sign in or create a free account to start your subscription');
      onOpenChange(false);
      navigate('/login');
      return;
    }
    setBusy(lookupKey);
    try {
      await startCheckout(lookupKey);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout. Please try again.');
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-slate-900 border border-amber-500/30 text-white" data-testid="plan-selection-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Crown className="h-6 w-6 text-amber-400" />
            Choose Your Plan
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Start with a 1-day free trial. Cancel anytime from your billing portal.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          {/* Monthly */}
          <div className="relative rounded-2xl border border-amber-500/40 bg-gradient-to-b from-slate-800 to-slate-900 p-6 flex flex-col" data-testid="plan-card-monthly">
            <div className="absolute -top-3 left-6 bg-amber-400 text-slate-900 text-xs font-bold px-3 py-1 rounded-full">
              LAUNCH OFFER
            </div>
            <h3 className="text-lg font-semibold text-amber-300">Monthly</h3>
            <div className="mt-3 flex items-end gap-2">
              <span className="text-4xl font-extrabold text-white">${monthly?.price ?? '19.99'}</span>
              <span className="text-slate-400 mb-1">/mo</span>
            </div>
            <p className="text-xs text-amber-400/80 mt-1">1-day free trial, then ${monthly?.price ?? '19.99'}/month</p>
            <Button
              onClick={() => handleChoose(monthly?.lookup_key || 'aloha_premium_monthly')}
              disabled={busy}
              className="mt-5 w-full bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-slate-900 font-bold"
              data-testid="choose-monthly-btn"
            >
              {busy === (monthly?.lookup_key || 'aloha_premium_monthly')
                ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start Free Trial'}
            </Button>
          </div>

          {/* Annual */}
          <div className="relative rounded-2xl border border-slate-700 bg-gradient-to-b from-slate-800 to-slate-900 p-6 flex flex-col" data-testid="plan-card-annual">
            <div className="absolute -top-3 left-6 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              BEST VALUE
            </div>
            <h3 className="text-lg font-semibold text-emerald-300">Annual</h3>
            <div className="mt-3 flex items-end gap-2">
              <span className="text-4xl font-extrabold text-white">${annual?.price ?? '99.99'}</span>
              <span className="text-slate-400 mb-1">/yr</span>
            </div>
            <p className="text-xs text-emerald-400/80 mt-1">1-day free trial, then ${annual?.price ?? '99.99'}/year</p>
            <Button
              onClick={() => handleChoose(annual?.lookup_key || 'aloha_premium_annual')}
              disabled={busy}
              variant="outline"
              className="mt-5 w-full border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 font-bold"
              data-testid="choose-annual-btn"
            >
              {busy === (annual?.lookup_key || 'aloha_premium_annual')
                ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start Free Trial'}
            </Button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          {PERKS.map((perk) => (
            <div key={perk} className="flex items-center gap-2 text-sm text-slate-300">
              <Check className="h-4 w-4 text-amber-400 flex-shrink-0" />
              {perk}
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
          <Sparkles className="h-3 w-3" /> Secure checkout by Stripe. We never store your card details.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default PlanSelectionModal;
